/**
 * EnterpriseDispatchTab — pure-display Dispatch Register table.
 *
 * Extracted from Enterprise.jsx (iter200). Read-only view; relies on parent
 * for filtered data + search/filter state.
 */
import React from 'react';
import { Search } from 'lucide-react';
import ProductCodeBadge from '../ProductCodeBadge';

const fmt = (n) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(n || 0);

export default function EnterpriseDispatchTab({
  dispatch,
  filteredDispatch,
  searchDispatch,
  setSearchDispatch,
  filterInvType,
  setFilterInvType,
}) {
  return (
    <div className="space-y-3" data-testid="dispatch-register">
      <div className="flex gap-2 items-center flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={12} className="absolute left-2.5 top-2.5 text-zinc-500" />
          <input value={searchDispatch} onChange={e => setSearchDispatch(e.target.value)}
            placeholder="Search by client, product, batch ID, invoice..."
            data-testid="search-dispatch"
            className="w-full bg-zinc-900 border border-zinc-800 rounded-lg pl-7 pr-3 py-2 text-zinc-300 text-xs focus:outline-none focus:border-amber-500" />
        </div>
        <select value={filterInvType} onChange={e => setFilterInvType(e.target.value)}
          data-testid="dispatch-type-filter"
          className="bg-zinc-900 border border-zinc-800 rounded-lg px-2.5 py-2 text-zinc-400 text-xs focus:outline-none">
          {['All', 'GST', 'NON-GST'].map(s => <option key={s} value={s} className="bg-zinc-900">{s}</option>)}
        </select>
        <span className="text-zinc-600 text-[10px] font-mono">{filteredDispatch.length}/{dispatch.length}</span>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]" data-testid="dispatch-table">
          <thead>
            <tr className="border-b border-zinc-800 text-zinc-500 text-[10px] uppercase">
              {['Date', 'Invoice', 'Type', 'Client', 'Products', 'Qty', 'Batch IDs', 'Total', 'Status'].map(h => (
                <th key={h} className="text-left px-3 py-3 font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredDispatch.map(d => (
              <tr key={d.order_id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30 align-top">
                <td className="px-3 py-2 text-zinc-500 text-xs font-mono">{d.date}</td>
                <td className="px-3 py-2 font-mono text-amber-400 text-xs font-bold">{d.invoice_number || '—'}</td>
                <td className="px-3 py-2">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${d.invoice_type === 'GST' ? 'bg-green-900/40 text-green-300 border-green-700' : 'bg-zinc-800 text-zinc-400 border-zinc-700'}`}>
                    {d.invoice_type}
                  </span>
                </td>
                <td className="px-3 py-2 text-zinc-200 text-xs">{d.client}</td>
                <td className="px-3 py-2 text-zinc-300 text-xs">
                  {(d.items || []).map((it, i) => (
                    <div key={`${d.order_id}-prod-${it.product_id || it.product_name || i}`} className="truncate max-w-[220px] flex items-center gap-1.5" title={it.product_name}>
                      <ProductCodeBadge name={it.product_name} code={it.product_code} />
                      <span>• {it.product_name}</span>
                    </div>
                  ))}
                </td>
                <td className="px-3 py-2 text-zinc-300 text-xs font-mono">
                  {(d.items || []).map((it, i) => (
                    <div key={`${d.order_id}-qty-${it.product_id || it.product_name || i}`}>{it.quantity} {it.unit}</div>
                  ))}
                </td>
                <td className="px-3 py-2 text-xs">
                  {(d.batch_ids || []).length ? (
                    <div className="flex flex-wrap gap-1">
                      {d.batch_ids.slice(0, 4).map((b) => (
                        <span key={`${d.order_id}-bid-${b}`} className="text-[9px] font-mono bg-amber-900/30 text-amber-300 border border-amber-700/40 px-1.5 py-0.5 rounded">{b}</span>
                      ))}
                      {d.batch_ids.length > 4 && <span className="text-[9px] text-zinc-500">+{d.batch_ids.length - 4}</span>}
                    </div>
                  ) : <span className="text-zinc-700 text-[10px]">—</span>}
                </td>
                <td className="px-3 py-2 font-mono text-zinc-200 text-xs">₹{fmt(d.total_amount)}</td>
                <td className="px-3 py-2">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${
                    d.payment_status === 'Paid' ? 'bg-green-900/40 text-green-300 border-green-700'
                    : d.payment_status === 'Partial' ? 'bg-amber-900/40 text-amber-300 border-amber-700'
                    : 'bg-red-900/30 text-red-400 border-red-800'}`}>
                    {d.payment_status || 'Unpaid'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredDispatch.length === 0 && (
          <div className="text-center py-12 text-zinc-600 text-xs">No dispatched bills yet</div>
        )}
      </div>
    </div>
  );
}
